import type {
  EditorElement,
  InlineElement,
} from '@wix/component-protocol/schema';
import type { CompRef } from '@wix/document-services-types';

import { hash } from '../utils';
import type { AbstractVirtualManifestBuilder } from './AbstractVirtualManifestBuilder';
import { IVirtualBuilder } from './types';
import { DependencyToken, IDependencyAPI } from '../dependencies';
import type { EditorReactComponentExtensionType } from '../stores';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export interface StandaloneComponentMetadata {
  componentType?: string;
  extensionType?: EditorReactComponentExtensionType;
}

export class StandaloneBuilder implements IVirtualBuilder {
  constructor(private readonly parent: AbstractVirtualManifestBuilder) {}

  withEditorElement(
    editorElement: EditorElement | InlineElement,
    componentMetadata?: StandaloneComponentMetadata,
  ) {
    this.parent.withEditorElement(
      getUnexistingEditorPointer(),
      editorElement,
      componentMetadata,
    );

    return this;
  }

  /**
   * keep for backward compatibility with Brands panel product
   */
  withStyleHandlers(handlers: {
    getStyles: () => {
      cssProperties?: Record<string, string>;
      cssCustomProperties?: Record<string, string>;
    };
    setStyles: (styles: {
      cssProperties?: Record<string, string>;
      cssCustomProperties?: Record<string, string>;
    }) => void;
    getDefaultValue: (key: string) => string;
    removeStyles: (keys: string[]) => void;
  }) {
    if (handlers.getStyles) {
      this.parent.overrideDependencyMethod(
        DependencyToken.UNKNOWN_COMPONENT_STYLES_GET,
        () => () => {
          return handlers.getStyles();
        },
      );
    }

    if (handlers.setStyles) {
      this.parent.overrideDependencyMethod(
        DependencyToken.UNKNOWN_COMPONENT_STYLES_UPDATE,
        () => (_editorPointer, styles) => {
          handlers.setStyles(styles);
        },
      );
    }

    if (handlers.removeStyles) {
      this.parent.overrideDependencyMethod(
        DependencyToken.UNKNOWN_COMPONENT_STYLES_REMOVE,
        () => (_editorPointer, styles) => {
          handlers.removeStyles(styles);
        },
      );
    }

    return this;
  }

  contributeExternalAPI<TToken extends DependencyToken>(
    token: TToken,
    cb: (
      api: Extract<IDependencyAPI, { type: TToken }>['api'],
    ) => Extract<IDependencyAPI, { type: TToken }>['api'],
  ) {
    // @ts-expect-error xxx
    this.parent.overrideDependencyMethod<TToken>(token, cb);

    return this;
  }

  build(manifestId: string) {
    return this.parent.build(manifestId);
  }
}

export const UNEXISTING_COMPONENT_PREFIX = 'UNEXISTING_COMPONENT_';

function getUnexistingEditorPointer() {
  const id = `${UNEXISTING_COMPONENT_PREFIX}${hash()}`;

  return {
    documentPointer: {
      id,
      type: 'DESKTOP',
    },
    innerElementPath: [],
    asUniqueKey: id,
  } as EditorPointer<CompRef>;
}
